import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ChevronDown, MapPin, Phone, Clock } from "lucide-react";
import { IMAGES, CONTACT } from "../lib/menuData";
import { OpenBadge } from "./OpenBadge";
import { fetchSpecialDays, specialFor, fmtDateDE } from "../lib/hours";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible"
      className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [special, setSpecial] = useState(null);

  useEffect(() => {
    fetchSpecialDays().then((days) => setSpecial(specialFor(days, new Date())));
  }, []);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] w-full overflow-hidden grain">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Sri Lanka – Ceylon" className="h-full w-full object-cover object-center" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-teal-dark/95 via-teal-dark/55 to-teal-dark/20" />
      <div className="absolute inset-0 bg-gradient-to-b from-teal-dark/70 via-transparent to-teal-dark" />

      <motion.div style={{ opacity: fade }}
        className="relative z-10 min-h-[100svh] max-w-7xl mx-auto px-5 md:px-10 flex flex-col justify-center py-28 md:py-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="overline mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-gold/60 inline-block" />
          Authentische Küche · 10 % Rabatt auf alles
        </motion.div>

        <h1 className="font-serif font-medium text-cream leading-[0.92] tracking-tight
          text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          <Line i={0}>Ceylon</Line>
          <Line i={1} className="text-gold italic">Royal</Line>
          <Line i={2}>Kitchen</Line>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-8 max-w-md text-base md:text-lg text-cream/75 leading-relaxed">
          Authentische srilankische & südindische Küche mit langjähriger Erfahrung –
          frisch und würzig zubereitet. Online bestellen mit 10 % Rabatt:
          zur Abholung oder als Lieferung direkt zu dir nach Hause.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <a href="#speisekarte" data-testid="hero-order-btn"
            className="group flex items-center gap-2 bg-gold text-teal-dark px-7 py-3.5 rounded-full font-medium hover:bg-gold-light transition-colors">
            <Phone size={17} /> Jetzt bestellen
          </a>
          <a href="#speisekarte" data-testid="hero-menu-btn"
            className="link-underline text-cream/90 px-2 py-3.5 font-medium">
            Zur Speisekarte
          </a>
          <span className="flex items-center gap-2 text-cream/60 text-sm ml-1">
            <MapPin size={16} className="text-gold" /> Wilhelmstraße 43, Karlsruhe
          </span>
          <OpenBadge />
        </motion.div>

        {special && (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
            data-testid="special-hours-notice"
            className="mt-6 inline-flex items-start gap-3 rounded-sm border border-gold/50 bg-gold/10 px-5 py-4 text-left"
          >
            <Clock size={18} className="text-gold mt-0.5 shrink-0" />
            <p className="text-sm text-cream/85 leading-relaxed">
              {special.kind === "closed" ? (
                <>
                  <b className="text-gold">Heute, {fmtDateDE(special.date)}</b> Wir bleiben heute <b className="text-cream">geschlossen</b>
                  {special.note && <> – {special.note}</>}. Bestellungen sind heute leider nicht möglich.
                </>
              ) : (
                <>
                  <b className="text-gold">Heute, {fmtDateDE(special.date)}</b> Wir haben heute von{" "}
                  <b className="text-cream">{special.open} – {special.close} Uhr</b> für dich geöffnet
                  {special.note && <> – {special.note}</>}. Bestellungen sind in diesem Zeitraum möglich.
                </>
              )}
            </p>
          </motion.div>
        )}
      </motion.div>

      <motion.div style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-gold">
        <span className="overline text-[0.6rem]">Scrollen</span>
        <ChevronDown className="animate-floaty" size={20} />
      </motion.div>
    </section>
  );
};
